/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import * as React from 'react';
import {
  TextButton,
  Layout,
  LayoutItem,
  Text,
  ProgressIndicator,
  Headline,
  TextLink,
  Tag,
} from '@oneaudi/unified-web-components';
import {
  breakpointsMap,
  SYS_SPACE_FIXED_100,
  SYS_SPACE_FIXED_150,
  SYS_SPACE_FIXED_300,
  SYS_SPACE_RELATIVE_SM,
} from '@oneaudi/unified-web-common';
import type { DealerContextServiceV1 } from '@oneaudi/dealer-context-service';
import { useI18n } from '@oneaudi/i18n-context';
// import { transformServices } from '@oneaudi/audi-partner-utils';
import styled from 'styled-components';
import { messages } from '../../../i18n/messages';
import { useTrackingManager } from '../../../hooks/useTrackingManager';
import { useMedia } from '../../../hooks/useMedia';
import { Image } from './Image';
import { FiveStarRating } from '../../common/FiveStarRating/FiveStarRating';
import { LegalDisclaimer } from '../../common/LegalDisclaimer/LegalDisclaimer';
import { generateMercantileValue } from '../../../utils/generateMercantileValue';
import mapContext from '../../../contexts/map-context';
import { getRatingSummary, getReviews } from '../../../api';
import { ActionTypes } from '../../../contexts/reducers/map-reducer';
import {
  connectDealerToDealerContext,
  disconnectDealerFromDealerContext,
} from '../../../utils/connectDealer';
import { transformServices } from '../../../utils/transformService';

const isLegacy = process.env.IS_LEGACY;

const StyledImage = styled.figure`
  position: relative;
  margin: ${SYS_SPACE_RELATIVE_SM} 0;
  /* create aspect ratio box with an aspect ratio of 16:9 */
  padding: 0 0 56.25% 0;
  overflow: hidden;

  > img {
    display: block;
    position: absolute;
    width: 100%;
    height: auto;
    border-radius: ${isLegacy ? '0px' : 'var(--Sys-BorderRadius-MD, 20px)'};
  }
`;

const StyledHeadlineAsLink = styled.div`
  > a {
    border-bottom: 0;
    font-weight: normal;
    ::before {
      content: none;
    }
  }
`;

const FiveStarRow = styled.div`
  margin: 12px 0;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const TextStyled = styled(Text)`
  display: flex;
  align-items: center;
`;

const ReviewButtonLoader = styled(ProgressIndicator)`
  margin: 0 0 12px 0;
`;

const ReviewButtonStyled = styled(TextLink)`
  text-decoration: underline;
  min-width: auto;
  min-height: 0;
  height: auto;
  border: none;
  padding: 0;
  margin: 0 0 12px 0;

  svg {
    display: none;
  }
`;

interface PartnerBusinessCardHeaderProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  partner: any;
  // eslint-disable-next-line react/require-default-props
  isNarrow?: boolean;
  // eslint-disable-next-line react/require-default-props
  showImage?: boolean;
  // eslint-disable-next-line react/require-default-props
  isLinkedToPWS?: boolean;
  displayFiveStarRating?: boolean;
  fiveStarRating?: number;
  flyoutCta: () => void;
}

export const PartnerBusinessCardHeader = (
  props: PartnerBusinessCardHeaderProps,
): React.ReactElement => {
  const {
    selectedDealerId,
    ratingSummary,
    dealerReviews,
    dealerContextService,
    connectedDealer,
    connectDealer,
    autoConnectDealer,
  } = mapContext.useMapState();

  const [isFiveStarDataLoading, setIsFiveStarDataLoading] = React.useState(false);
  const [isFlyoutButtonDisplayed, setIsFlyoutButtonDisplayed] = React.useState(false);
  const {
    partner,
    isNarrow,
    showImage = true,
    isLinkedToPWS = true,
    displayFiveStarRating = false,
    fiveStarRating,
    flyoutCta,
  } = props;
  const trackingManager = useTrackingManager();

  const dispatch = mapContext.useMapDispatch();

  // const myAudiLinkLabel = useI18n(messages.myAudiLinkLabel);
  const ratingLabel = useI18n(messages.five_star_ratings);
  const reviewLabel = useI18n(messages.five_star_reviews);

  const partnerServiceLabels = new Map([
    ['sales', useI18n(messages.partnerServiceSales)],
    ['sales-r8', useI18n(messages.partnerServiceSalesR8)],
    ['service-r8', useI18n(messages.partnerServiceServiceR8)],
    ['service', useI18n(messages.partnerServiceService)],
    ['etron', useI18n(messages.partnerServiceEtron)],
    ['usedcars', useI18n(messages.partnerServiceUsedcars)],
  ]);

  const services = transformServices(
    partner.services,
    partner.additionalServices,
    partnerServiceLabels,
  );

  // TODO Check imgWidth value
  const imgWidth = 328;

  const isViewportM = useMedia(`only screen and (min-width: ${breakpointsMap.md}) `);
  // const isViewportL = useMedia(`only screen and (min-width: ${breakpointsMap.lg}px) `);

  const showColumnItemPicture = (showImage && isNarrow) || (!isViewportM && !isNarrow);
  const partnerName = partner.nameEnhanced;

  React.useEffect(() => {
    if (ratingSummary) {
      setIsFlyoutButtonDisplayed(true);
    } else if (selectedDealerId && displayFiveStarRating && fiveStarRating) {
      setIsFiveStarDataLoading(true);
      const ratingSummaryPromise = getRatingSummary(selectedDealerId);
      const reviewsPromise = getReviews(selectedDealerId);
      Promise.all([ratingSummaryPromise, reviewsPromise]).then((results) => {
        const [summary, reviews] = results;
        if (summary && reviews) {
          setIsFlyoutButtonDisplayed(true);
          dispatch({ type: ActionTypes.setRatingSummary, payload: summary });
          dispatch({ type: ActionTypes.setDealerReviews, payload: reviews });
        }
        setIsFiveStarDataLoading(false);
      });
    }
  }, [selectedDealerId, ratingSummary]);

  const totalRatings = ratingSummary?.totalRatings;
  const reviewCount = dealerReviews?.length;
  const ratingText = (totalRatings && `${totalRatings} ${ratingLabel}`) || '';
  const reviewText = reviewCount && reviewCount > 0 && `${reviewCount} ${reviewLabel}`;
  const ratingsAndReviewsText = (() => {
    if (ratingText && reviewText) {
      return `${ratingText}, ${reviewText}`;
    }
    return ratingText || reviewText;
  })();
  const ratingsAndReviewsButton = (() => {
    if (displayFiveStarRating && fiveStarRating && isFiveStarDataLoading) {
      return <ReviewButtonLoader type="infinite" variant="bar" size="sm" />;
    }
    if (isFlyoutButtonDisplayed) {
      return (
        <ReviewButtonStyled variant="standalone" size="sm" onClick={flyoutCta}>
          {ratingsAndReviewsText}
        </ReviewButtonStyled>
      );
    }
    return null;
  })();

  const partnerImage = (
    <StyledImage>
      <Image
        loadingAnimation="fade"
        src={`${partner.locationImageUrl}?width=${imgWidth}`}
        data-testid="image"
        alt={partner.displayName}
        sizes={[
          {
            condition: `(max-width: ${imgWidth}px)`,
            value: `${imgWidth}px`,
          },
          {
            condition: '(max-width: 639px)',
            value: '656px',
          },
          {
            condition: '(max-width: 1023px)',
            value: `${imgWidth}px`,
          },
          {
            value: '656px',
          },
        ]}
        srcSet={[
          {
            src: `${partner.locationImageUrl}?width=${imgWidth}`,
            desc: `${imgWidth}w`,
          },
          {
            src: `${partner.locationImageUrl}?width=656`,
            desc: '656w',
          },
        ]}
      />
    </StyledImage>
  );

  return (
    <Layout
      direction={{ xs: 'column', lg: 'column' }}
      wrap="nowrap"
      align="stretch"
      justify="flex-start"
    >
      <LayoutItem align="stretch">
        {partner.locationImageUrl && showColumnItemPicture && (
          <LayoutItem margin={[0, 0, '24px', 0]}>
            {isLinkedToPWS ? (
              <a
                onClick={() =>
                  trackingManager.click(
                    'exit_link',
                    partner.url,
                    'image',
                    '',
                    partnerName,
                    '1',
                    'pws link',
                  )
                }
                href={partner.url}
                target={isNarrow ? '_blank' : ''}
                rel="noreferrer"
              >
                {partnerImage}
              </a>
            ) : (
              <>{partnerImage}</>
            )}
          </LayoutItem>
        )}
        <Headline
          tag="h2"
          variant={{ order: '3', type: 'headline', weight: 'normal' }}
          data-testid="name"
        >
          {isLinkedToPWS ? (
            <StyledHeadlineAsLink>
              <TextLink
                variant="standalone"
                size="md"
                onClick={() =>
                  trackingManager.click(
                    'exit_link',
                    partner.url,
                    'headline',
                    partnerName,
                    partnerName,
                    '1',
                    'pws link',
                  )
                }
                href={partner.url}
                target={isNarrow ? '_blank' : ''}
                rel="noreferrer"
              >
                <Headline tag="span" variant={{ order: '3', type: 'headline', weight: 'normal' }}>
                  {partnerName}
                </Headline>
              </TextLink>
            </StyledHeadlineAsLink>
          ) : (
            partnerName
          )}
        </Headline>
        {displayFiveStarRating && fiveStarRating && (
          <>
            <FiveStarRow>
              <FiveStarRating rating={fiveStarRating} />
              <TextStyled margin={[0, 0, 0, '8px']}>
                {generateMercantileValue(fiveStarRating || 0)}
              </TextStyled>
              {!ratingsAndReviewsButton && <LegalDisclaimer />}
            </FiveStarRow>
            {ratingsAndReviewsButton}
          </>
        )}
        {/* <Headline
          data-testid="services"
          variant={{ order: '4', type: 'headline', weight: 'normal' }}
          tag="span"
        >
          {services}
        </Headline> */}
        <LabelsContainer>
          {services.map((service) => (
            <Tag label={service} variant="read-only" />
          ))}
        </LabelsContainer>
      </LayoutItem>
      {connectDealer && connectedDealer !== partner.partnerId && (
        <TextButton
          variant="primary"
          icon="poi"
          iconPosition="leading"
          size={isLegacy ? 'sm' : ('xs' as any)}
          isFullWidth
          margin={[0, 0, SYS_SPACE_FIXED_300]}
          onClick={() =>
            connectDealerToDealerContext(
              partner.partnerId,
              dealerContextService as DealerContextServiceV1,
              dispatch,
            )
          }
        >
          {!connectedDealer
            ? useI18n(messages.saveAsConnectedDealer)
            : useI18n(messages.changeConnectedDealer)}
        </TextButton>
      )}

      {connectDealer && connectedDealer === partner.partnerId && (
        <TextButton
          variant="primary"
          icon="poi-active"
          iconPosition="leading"
          size={isLegacy ? 'sm' : ('xs' as any)}
          isFullWidth
          margin={[0, 0, SYS_SPACE_FIXED_300]}
          onClick={() =>
            disconnectDealerFromDealerContext(
              dealerContextService as DealerContextServiceV1,
              dispatch,
              autoConnectDealer,
            )
          }
        >
          {useI18n(messages.disconnectDealer)}
        </TextButton>
      )}
    </Layout>
  );
};

const LabelsContainer = styled.div`
  margin: ${SYS_SPACE_FIXED_150} 0 ${SYS_SPACE_FIXED_300};
  display: inline-flex;
  gap: ${SYS_SPACE_FIXED_100} ${SYS_SPACE_FIXED_100};
  flex-wrap: wrap;
`;
